import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Bell, Tag, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function NotificationBell() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (user) loadCount();
    else { setUnread(0); setItems([]); }
  }, [user, loadCount]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o) {
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
        if (unread > 0) {
          await api.post("/notifications/read-all");
          setUnread(0);
        }
      } catch { /* ignore */ }
    }
  };

  const discountLabel = (n) => (n.coupon_type === "percent" ? `-${n.coupon_value}%` : `-$${n.coupon_value}`);

  const goTo = (n) => {
    setOpen(false);
    navigate(`/product/${n.product_id}`);
  };

  const dismiss = async (e, n) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
    try { await api.delete(`/notifications/${n.id}`); } catch { /* ignore */ }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors" data-testid="notif-bell" aria-label="notifications">
          <Bell className="h-5 w-5 text-[#0F172A]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-[#E2E8F0] font-display font-bold text-[#0F172A]">{t("notif.title")}</div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#94A3B8]" data-testid="notif-empty">{t("notif.empty")}</div>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => goTo(n)} data-testid={`notif-item-${n.id}`}
                className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50 transition-colors ${!n.read ? "bg-sky-50/50" : ""}`}>
                <span className="h-8 w-8 rounded-full bg-amber-50 text-[#F59E0B] flex items-center justify-center shrink-0 mt-0.5"><Tag className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-[#0F172A] truncate">{n.product_title}</span>
                  <span className="block text-xs text-[#475569]">
                    {t("notif.couponIn")}: <b className="text-[#0EA5E9]">{n.code}</b> {discountLabel(n)} {t("notif.by")} {n.seller_name}
                  </span>
                </span>
                <span onClick={(e) => dismiss(e, n)} data-testid={`notif-dismiss-${n.id}`}
                  className="text-[#94A3B8] hover:text-red-500 shrink-0 mt-0.5 cursor-pointer" aria-label="dismiss">
                  <X className="h-4 w-4" />
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
